import { useParams } from "@tanstack/react-router";
import { useEffect, useMemo, useState, type ReactNode } from "react";

import type { Environment } from "@magicweave/api";

import { useEnvironments } from "../api/hooks";
import { EnvironmentContext, type Selection } from "./environment-context";

/**
 * Holds the shell's one environment choice.
 *
 * A route that names an environment always wins, so following a link never
 * silently moves you somewhere else. Otherwise it is the last choice made, and
 * otherwise the environment somebody is most likely to have meant.
 *
 * That is production when production has players in it — checking on your game
 * means checking on the one people are playing, and every screen this chooses
 * for is a read. On a project that has not shipped yet it is whichever
 * environment does have players, because landing a new reader on an empty
 * production and telling them nothing has arrived is alarming, accurate and
 * useless: nothing was ever going to arrive there.
 */
export function EnvironmentProvider({
  projectId,
  children,
}: {
  projectId: string | undefined;
  children: ReactNode;
}) {
  const params = useParams({ strict: false });
  const fromRoute = params.envId;
  const environments = useEnvironments(projectId ?? "");
  const [chosen, setChosen] = useState<string | undefined>(() => remembered(projectId));

  // A route that names one is a decision already made; recording it means
  // leaving that route does not throw the choice away.
  useEffect(() => {
    if (!projectId || !fromRoute || fromRoute === chosen) return;
    setChosen(fromRoute);
    remember(projectId, fromRoute);
  }, [projectId, fromRoute, chosen]);

  const loaded = environments.data;
  const value = useMemo<Selection>(() => {
    const known = loaded ?? [];
    const envId =
      fromRoute ??
      (chosen && known.some((e) => e.id === chosen) ? chosen : undefined) ??
      likeliest(known)?.id;
    return {
      envId,
      choose: (id: string) => {
        setChosen(id);
        remember(projectId, id);
      },
    };
  }, [fromRoute, chosen, loaded, projectId]);

  return <EnvironmentContext value={value}>{children}</EnvironmentContext>;
}

function likeliest(known: Environment[]): Environment | undefined {
  const production = known.find((e) => e.kind === "production");
  if (production && production.players > 0) return production;
  return known.find((e) => e.players > 0) ?? production ?? known[0];
}

function remember(projectId: string | undefined, envId: string): void {
  if (!projectId) return;
  try {
    localStorage.setItem(`mw:env:${projectId}`, envId);
  } catch {
    // A browser with site data blocked still navigates; it just forgets.
  }
}

function remembered(projectId: string | undefined): string | undefined {
  if (!projectId) return undefined;
  try {
    return localStorage.getItem(`mw:env:${projectId}`) ?? undefined;
  } catch {
    return undefined;
  }
}
